import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import "../invoice/invoice.css";
import Logo from "../../assests/papalogo.png";
import invoicefooter from "../../assests/invoicefooter.jpeg";
import phoneinvoice from "../../assests/call_invoice.png";
import emailinvoice from "../../assests/email_invoice.jpg";
import websiteinvoice from "../../assests/website_invoice.png";
import { Button, Card, Form, notification, Switch } from "antd";
import { useNavigate } from "react-router-dom";
import { API } from "../../config/api";
import { POST } from "../../utils/apicalls";

import moment from "moment";
import PageHeader from "../../admin/pageHeader/pageheader";

const InvoiceGeneratorHr: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [includeGST, setIncludeGST] = useState(false);

  const [invoiceDetails, setInvoiceDetails] = useState({
    billNumber: "",
    date: "",
    customerName: "",
    customerAddress: "",
    total: 0,
    discount: 0,
    discountAmount: 0,
    afterDiscount: 0,
    gst: 0,
    finalTotal: 0,
    customerGSTNumber: "",
    paymentMethod: "",
  });
  const [items, setItems] = useState([
    {
      description: "",
      HSNSAC: "",
      quantity: 1,
      price: 0,
      subtotal: 0,

    },
  ]);



  const submitHandler = async (values: any) => {
    try {
      if (!isDownloading) {
        return;
      }
      setIsLoading(true);

      // Create invoice with array fields for items
      let invoiceData = {
        billNumber: invoiceDetails.billNumber,
        billDate: moment(invoiceDetails.date).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        customerName: invoiceDetails.customerName,
        customerAddress: invoiceDetails.customerAddress,
        discount: Number(invoiceDetails.discount),
        subTotal: items.map((item) => Number(item.subtotal)),
        finalAmount: Number(invoiceDetails.finalTotal),
        GST: includeGST ? String(invoiceDetails.gst) : "0",
        customerGSTNumber: invoiceDetails.customerGSTNumber || "",
        paymentMethod: invoiceDetails.paymentMethod || "",
        price: items.map((item) => Number(item.price)),
        quantity: items.map((item) => Number(item.quantity)),
        description: items.map((item) => item.description),
        HSNSAC: items.map((item) => item.HSNSAC),
      };

      const invoiceResponse: any = await POST(API.CREATE_INVOICE, invoiceData);

      if (invoiceResponse?.status && invoiceResponse?.data?.id) {
        notification.success({ message: "Invoice created successfully!" });
        await generatePDF();
        form.resetFields();
        navigate(-1);
      } else {
        notification.error({
          message: "Failed to create invoice - no ID returned",
        });
      }
    } catch (error) {
      console.error("Operation error:", error);
      notification.error({
        message: "Something went wrong! Please try again later",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemChange = (
    index: number,
    field: string,
    value: string | number
  ) => {
    const newItems = [...items];
    const item: any = newItems[index];
    item[field] = value;

    // Recalculate subtotal
    if (field === "quantity" || field === "price") {
      item.subtotal = item.quantity * item.price;
    }

    setItems(newItems);

    // Update total and final total
    recalculateTotals(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        HSNSAC: "",
        quantity: 1,
        price: 0,
        subtotal: 0,
      },
    ]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, idx) => idx !== index);
      setItems(updatedItems);
      recalculateTotals(updatedItems);
    } else {
      notification.warning({
        message: "Cannot remove all items",
        description: "At least one item is required.",
      });
    }
  };

  const handleGSTToggle = (checked: boolean) => {
    setIncludeGST(checked);
    recalculateTotals(items, invoiceDetails.discount, checked);
  };

  const handleDiscountChange = (value: any) => {
    const discount = Number(value) || 0; // treat as percentage
    setInvoiceDetails((prev) => ({
      ...prev,
      discount,
    }));
    recalculateTotals(items, discount, includeGST);
  };

  const recalculateTotals = (
    itemsList = items,
    discount = invoiceDetails.discount,
    gstEnabled = includeGST
  ) => {
    // Calculate subtotal (sum of all item subtotals)
    const subtotal = itemsList.reduce((sum, item) => sum + Number(item.subtotal), 0);

    // Calculate discount amount (percentage of subtotal)
    const discountAmount = parseFloat(((subtotal * discount) / 100).toFixed(2));

    // Calculate amount after discount
    const afterDiscount = Math.max(0, subtotal - discountAmount);

    // Calculate GST if enabled (18% of discounted amount)
    const gst = gstEnabled ? parseFloat((afterDiscount * 0.18).toFixed(2)) : 0;

    // Calculate final total (discounted amount + GST)
    const finalTotal = afterDiscount + gst;

    // Update all calculation fields at once
    setInvoiceDetails((prev) => ({
      ...prev,
      total: subtotal,
      discountAmount: discountAmount,
      afterDiscount: afterDiscount,
      gst: gst,
      finalTotal: finalTotal
    }));
  };
  const generatePDF = async () => {
    try {
      setIsDownloading(true);

      // Add a class to the body or container when downloading
      document.body.classList.add('hide-add-item');

      // Hide elements that shouldn't appear in the PDF
      const actionColumns = document.querySelectorAll(".actions-column");
      const buttons = document.querySelectorAll(".button-section, .add-item-section");

      actionColumns.forEach(col => col.classList.add("hidden-while-downloading"));
      buttons.forEach(btn => btn.classList.add("hidden-while-downloading"));

      const invoiceElement = document.getElementById("invoice-preview");
      if (!invoiceElement) throw new Error("Invoice preview element not found");

      // Add PDF-specific styling
      invoiceElement.classList.add("downloading-pdf");

      // Set specific dimensions to match A4 size (210mm × 297mm)
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = 297; // A4 height in mm

      // Calculate optimal sizing
      const html2canvasOptions = {
        scale: 1, // Higher scale for better quality
        useCORS: true,
        logging: false,
        width: invoiceElement.offsetWidth,
        height: invoiceElement.offsetHeight,
        windowWidth: invoiceElement.scrollWidth,
        windowHeight: invoiceElement.scrollHeight
      };

      const canvas = await html2canvas(invoiceElement, html2canvasOptions);

      // Create PDF with A4 dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Calculate proper scaling to fit content on A4
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Handle multi-page if content is taller than A4 height
      if (imgHeight > pdfHeight) {
        // Calculate how many pages we need
        const pageCount = Math.ceil(imgHeight / pdfHeight);

        // for (let i = 0; i < pageCount; i++) {
        //   // For pages after the first page, add a new page
        //   if (i > 0) {
        //     pdf.addPage();
        //   }

        // Calculate position to slice the canvas for current page
        // const sourceY = i * canvas.height / pageCount;
        const sourceHeight = canvas.height / pageCount;

        // Add sliced portion of the image to the current page
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          0,
          pdfWidth,
          pdfHeight,
          '',
          'FAST',
          // i * -pdfHeight // Offset to show correct portion
        );
      }
      else {
        // If content fits on one page, simply add the image
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          0,
          imgWidth,
          imgHeight
        );
      }

      // Save the PDF with a custom name
      pdf.save(`invoice_${invoiceDetails.billNumber}.pdf`);

    } catch (error) {
      console.error("PDF generation error:", error);
      notification.error({
        message: "Failed to generate PDF"
      });
    } finally {
      // Clean up - remove temporary classes
      document.body.classList.remove('hide-add-item');

      const invoiceElement = document.getElementById("invoice-preview");
      if (invoiceElement) {
        invoiceElement.classList.remove("downloading-pdf");
      }

      // Restore visibility of hidden elements
      const actionColumns = document.querySelectorAll(".actions-column");
      const buttons = document.querySelectorAll(".button-section, .add-item-section");

      actionColumns.forEach(col => col.classList.remove("hidden-while-downloading"));
      buttons.forEach(btn => btn.classList.remove("hidden-while-downloading"));

      setIsDownloading(false);
    }
  };


  const handleInputChange = (field: string, value: string | number) => {
    setInvoiceDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };


  return (
    <div className="invoice-container">
      <PageHeader title="Invoice Generator" />
      <Card>
        <Form form={form} onFinish={submitHandler}>
          <h2>Invoice Generator</h2>

          {/* Form Section */}
          <div className="form-section">
            <label>Bill number:</label>
            <input
              type="text"
              value={invoiceDetails.billNumber}
              onChange={(e) => {

                setInvoiceDetails((prev) => ({
                  ...prev,
                  billNumber: e.target.value,
                }));
              }}
            />
            <label>Date:</label>
            <input
              type="date"
              value={invoiceDetails.date}
              onChange={(e) => handleInputChange("date", e.target.value)}
            />

            <label>Customer Name:</label>
            <input
              type="text"
              value={invoiceDetails.customerName}
              onChange={(e) => handleInputChange("customerName", e.target.value)}
            />

            <label>Customer Address:</label>
            <textarea
              value={invoiceDetails.customerAddress}
              onChange={(e) => handleInputChange("customerAddress", e.target.value)}
            />

            <label>Customer GST Number:</label>
            <input
              type="text"
              value={invoiceDetails.customerGSTNumber}
              onChange={(e) => handleInputChange("customerGSTNumber", e.target.value)}
            />

            <label>Payment Method:</label>
            <input
              type="text"
              value={invoiceDetails.paymentMethod}
              onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
            />
          </div>

          <div className="togglesss">
            {/* GST Toggle */}
            <div className="gst-toggle">
              <label>Include GST:</label>
              <Switch checked={includeGST} onChange={handleGSTToggle} />
            </div>

            {/* Discount Section */}
            <div className="discount-section">
              <label>Discount:</label>
              <input
                type="number"
                value={invoiceDetails.discount}
                onChange={(e) => handleDiscountChange(Number(e.target.value))}
              />
            </div>

            {/* Hide the "Add Item" button during download */}
            <div className="add-item-section">
              {!isDownloading && <button type="button" onClick={addItem}>Add Item</button>}
            </div>
          </div>

          {/* Invoice Preview */}
          <Card style={{ margin: "0px", marginRight: "0px" }}>
            <div id="invoice-preview" className="invoice-preview">
              <div>
                <img
                  src={invoicefooter}
                  style={{ width: "100%" }}
                  alt="Invoice Footer"
                />

              </div>
              <div className="invoice-header">
                <div className="logo-container">
                  <img src={Logo} alt="Company Logo" className="company-logo" />
                  {includeGST && <h5><b>Tax Invoice</b></h5>}
                </div>



                <div>
                  <div className="payment-row">
                    <span className="payment-header">{!includeGST && <h3>Estimated Invoice</h3>}
                      {includeGST && <h3>Invoice</h3>}</span>
                  </div>
                  <div className="payment-row">
                    <span className="label">Bill No  </span>
                    <span className="colon">:</span>
                    <span className="value">{includeGST && <span>2024-25/</span>}  {invoiceDetails.billNumber}</span>
                  </div>
                  <div className="payment-row">
                    <span className="label">Date</span>
                    <span className="colon">:</span>
                    <span className="value">
                      {new Date(invoiceDetails.date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>

                  <div className="payment-row">
                    {includeGST && <div className="payment-row"><span className="label">POS</span><span className="colon">:</span><span className="value">KERALA (32) </span></div>}
                  </div>
                </div>
              </div>

              <div
                className="two"
                style={{
                  width: "300px",
                  wordWrap: "break-word",
                  overflow: "hidden",
                  textAlign: "left",
                }}
              >
                <p>   <b>Bill To:</b></p>
                <p> {invoiceDetails.customerName}
                  <p>{invoiceDetails.customerAddress}</p>
                  {includeGST && invoiceDetails.customerGSTNumber && (
                    <p>GST Number:<p>{invoiceDetails.customerGSTNumber}</p> </p>
                  )}
                </p>
              </div>

              <div className="one">
                <div className="two">
                  <p>
                    <b> Company Address:</b>
                    <br />
                    PAPA DIGITAL SOLUTION <br />
                    Blue Hill Tower , 3rd Floor
                    <br />
                    Kuzhikkatumoola,Kakkanad-kochi
                    <br />
                    682030 Kerala,INDIA
                  </p>
                  <p>
                    {includeGST && <strong>GST_Number:-32ATAPR8197G1ZE </strong>}
                  </p>
                </div>

                {includeGST && (
                  <div className="payment-container">
                    <h4 >PAYMENT METHOD</h4>
                    <div className="payment-details">
                      <div className="payment-row">
                        <span className="label">GPAY</span>
                        <span className="colon">:</span>
                        <span className="value">977 83 63 852</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">Account No</span>
                        <span className="colon">:</span>
                        <span className="value">2152202100000261</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">IFC Code</span>
                        <span className="colon">:</span>
                        <span className="value">PUNB0215220</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">Account Name</span>
                        <span className="colon">:</span>
                        <span className="value">PAPA DIGITAL SOLUTION</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">Bank Name</span>
                        <span className="colon">:</span>
                        <span className="value">PUNJAB NATIONAL BANK</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">Branch Name</span>
                        <span className="colon">:</span>
                        <span className="value">KAKKANAD,KUSUMAGIRI</span>
                      </div>
                    </div>
                  </div>
                )}

                {!includeGST && (
                  <div className="payment-container">
                    <h4>PAYMENT METHOD</h4>
                    <div className="payment-details">
                      <div className="payment-row">


                        <span className="label">GPAY</span>
                        <span className="colon">:</span>
                        <span className="value">977 83 63 852</span>

                      </div>
                      <div className="payment-row">
                        <span className="label">  Account No</span>
                        <span className="colon">:</span>
                        <span className="value"> 42191757548</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">  IFC Code</span>
                        <span className="colon">:</span>
                        <span className="value"> SBIN0070425</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">  Account Name </span>
                        <span className="colon">:</span>
                        <span className="value"> Farhana K N</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">  Bank Name </span>
                        <span className="colon">:</span>
                        <span className="value"> SBI</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">   Branch Name</span>
                        <span className="colon">:</span>
                        <span className="value"> Kizhakamabalam</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Table Section */}
              <Card>
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      {includeGST && <th>HSN/SAC</th>}
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Subtotal</th>
                      {!isDownloading && (
                        <th className="actions-column">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          {isDownloading ? (
                            <div
                              style={{
                                minHeight: '40px',
                                width: '100%',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                border: '1px solid #ccc',
                                padding: '4px'
                              }}
                            >
                              {item.description}
                            </div>
                          ) : (
                            <textarea
                              value={item.description}
                              onChange={(e) =>
                                handleItemChange(index, 'description', e.target.value)
                              }
                              style={{ height: 'auto', minHeight: '40px', width: '100%' }}
                              rows={Math.max(1, (item.description.match(/\n/g) || []).length + 1)}
                            />
                          )}
                        </td>
                        {includeGST &&
                          <td>
                            <input
                              type="text"
                              value={item.HSNSAC || "998361"} // Added default value of "123"
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "HSNSAC",
                                  e.target.value
                                )
                              }
                              style={{ height: "30px", width: "100%" }}
                            />
                          </td>
                        }
                        <td>
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "price",
                                Number(e.target.value)
                              )
                            }
                            style={{ height: "30px", width: "100%" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantity",
                                Number(e.target.value)
                              )
                            }
                            style={{ height: "30px", width: "100%" }}
                          />
                        </td>

                        <td>₹{item.subtotal}</td>
                        {!isDownloading && (
                          <td className="actions-column">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="remove-btn"
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>

              {/* Add Create Invoice button outside the table */}
              {!isDownloading && (
                <div
                  className="button-group"
                  style={{ marginTop: "20px", display: "flex", gap: "10px" }}
                >
                  <Button
                    type="primary"
                    onClick={generatePDF}
                    loading={isLoading}
                  >
                    Preview PDF
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      setIsDownloading(true);
                      form.submit();
                    }}
                    loading={isLoading}
                  >
                    Create & Download
                  </Button>
                </div>
              )}

              <div style={{ paddingTop: "20px" }}></div>
              <hr color="red" />
              <div className="main">
                <div className="main1">
                  <h3>TERMS AND CONDITIONS</h3>
                  <p>
                    This quotation will be valid for 30 days.
                    <br /> Payment is non-refundable once payment
                    <br /> has been made and work has started
                  </p>
                  {includeGST && (
                    <>
                      <p>1. Payment to be made by crossed DD/Cheque</p>
                      <p>
                        2. Every legal matter relating to this transaction
                        <br />
                        come under the jurisdiction of Ernakulam courts
                      </p>
                      <p>
                        3. We declare that this invoice shows the actual price
                        <br />
                        and that all particulars are true and correct
                      </p>
                    </>
                  )}
                  {!includeGST && <b>GST NOT INCLUDED</b>}
                </div>

                <div className="main2">
                  <div className="payment-row">
                    <span className="label"> {includeGST && <b>TAXABLE VALUE</b>} {!includeGST && <b>SubTotal Total</b>}</span>
                    <span className="colon">:</span>
                    <span className="value">₹{invoiceDetails.total.toFixed(2)}</span>
                  </div>
                  <div className="payment-row">
                    <span className="label">Discount (  {invoiceDetails.discount}%       )</span>
                    <span className="colon">:</span>
                    <span className="value">

                      ₹{((invoiceDetails.total * invoiceDetails.discount) / 100).toFixed(2)}
                    </span>
                  </div>
                  {includeGST && (
                    <>
                      <div className="payment-row">
                        <span className="label"> CGST (9%)</span>
                        <span className="colon">:</span>
                        <span className="value">   ₹
                          {(invoiceDetails.gst / 2).toFixed(2)}</span>
                      </div>
                      <div className="payment-row">
                        <span className="label">SGST (9%)</span>
                        <span className="colon">:</span>
                        <span className="value">   ₹
                          {(invoiceDetails.gst / 2).toFixed(2)}</span>
                      </div>
                      <p
                        style={{
                          backgroundColor: "#9e2118",
                          color: "white",
                          fontSize: "18px",
                        }}
                      >
                        <strong>Total:</strong> ₹
                        {invoiceDetails.finalTotal.toFixed(2)}
                      </p>
                    </>
                  )}
                  {!includeGST && (
                    <p
                      style={{
                        backgroundColor: "#9e2118",
                        color: "white",
                        fontSize: "18px",
                      }}
                    >
                      <strong> Total:</strong> ₹
                      {(invoiceDetails.total - invoiceDetails.discount).toFixed(2)}
                    </p>
                  )}
                  <hr />
                  <p>
                    FARHANA K N
                    <br /> ACCOUNTS MANAGER
                  </p>
                </div>
              </div>

              <div className="footer">
                <div style={{ paddingLeft: "40px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={phoneinvoice}
                      alt="Phone Icon"
                      style={{ width: "20px", marginRight: "10px" }}
                    />
                    <span>+91-956 21 21 117</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={emailinvoice}
                      alt="Email Icon"
                      style={{ width: "20px", marginRight: "10px" }}
                    />
                    <span>papaekmpapa@gmail.com</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={websiteinvoice}
                      alt="Website Icon"
                      style={{ width: "20px", marginRight: "10px" }}
                    />
                    <span>www.papadigitalsolution.com</span>
                  </div>
                </div>
                <p
                  style={{
                    textAlign: "center",
                    paddingTop: "0px",
                    fontSize: "20px",
                  }}
                >
                  <b>THANK YOU FOR YOUR BUSINESS</b>
                </p>
                <div>
                  <img
                    src={invoicefooter}
                    style={{ width: "100%", paddingTop: "43px" }}
                    alt="Invoice Footer"
                  />
                </div>
              </div>
            </div>
          </Card>
        </Form>
      </Card>
    </div>
  );
};

export default InvoiceGeneratorHr;